import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError, describeError, isApiError } from "../api/errors";
import type { Performance, Playlist } from "../api/types";
import { useServices } from "../app/services";
import { useResource } from "../app/useResource";
import { ConfirmAction, EnqueueButton } from "../components/cards";
import { Icon } from "../components/Icon";
import { EmptyState, PageHeader, ResourceView, Thumb } from "../components/ui";
import { performerNames } from "../lib/labels";
import { announce } from "../state/announcer";

/** 非公開プレイリストの中身。並べ替えは上下のボタンで（キーボードでも操作できる） */
export function PlaylistPage() {
  const { id = "" } = useParams();
  const { api } = useServices();
  const res = useResource(
    "playlists.list",
    async () => {
      const pl = (await api.getPlaylists()).find((x) => x.id === id);
      if (!pl) throw new ApiError(404, "not_found");
      const perfs = await api.getPerformances(pl.performance_ids);
      return { pl, perfs };
    },
    [id],
  );
  return (
    <div className="page">
      <ResourceView resource={res} what="プレイリスト">
        {({ pl, perfs }) => <PlaylistView key={pl.id + pl.revision} playlist={pl} performances={perfs} reload={res.reload} />}
      </ResourceView>
    </div>
  );
}

function PlaylistView({ playlist, performances, reload }: { playlist: Playlist; performances: Performance[]; reload: () => void }) {
  const { api } = useServices();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const byId = new Map(performances.map((p) => [p.id, p]));
  const suffix = api.mode === "mock" ? "（モック）" : "";

  const save = async (ids: string[], message: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.editPlaylist(playlist.id, { name: playlist.name, revision: playlist.revision, performance_ids: ids });
      announce(`${message}${suffix}`);
      reload();
    } catch (e) {
      const msg =
        isApiError(e) && e.status === 409 ? "ほかの画面で先に変更されていたため、最新の内容を読み込み直しました。" : describeError(e);
      setError(msg);
      announce(msg, "assertive");
      if (isApiError(e) && e.status === 409) reload();
    } finally {
      setBusy(false);
    }
  };

  const move = (i: number, dir: -1 | 1) => {
    const ids = [...playlist.performance_ids];
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    const title = byId.get(playlist.performance_ids[i])?.work.title ?? "曲";
    void save(ids, `「${title}」を${dir < 0 ? "上" : "下"}へ移しました`);
  };

  return (
    <>
      <PageHeader title={playlist.name} lead={`${playlist.performance_ids.length}曲 ・ 自分だけのプレイリスト`}>
        <p>
          <Link to="/library?tab=playlists">
            <Icon name="library" className="inline-icon" />
            プレイリストの一覧へ
          </Link>
        </p>
      </PageHeader>
      {error && (
        <p className="inline-note inline-note--danger" role="alert">
          {error}
        </p>
      )}
      {playlist.performance_ids.length === 0 ? (
        <EmptyState title="まだ曲が入っていません" icon="queue">
          曲のページの「プレイリストへ」から追加できます。
        </EmptyState>
      ) : (
        <ol className="queue__list">
          {playlist.performance_ids.map((pid, i) => {
            const p = byId.get(pid);
            if (!p) {
              return (
                <li key={`${pid}-${i}`} className="queue-item queue-item--unavailable">
                  <span className="queue-item__n">{i + 1}</span>
                  <div className="queue-item__body">
                    <p className="queue-item__title">見つからない歌唱</p>
                  </div>
                </li>
              );
            }
            return (
              <li key={`${pid}-${i}`} className={`queue-item${p.available ? "" : " queue-item--unavailable"}`}>
                <span className="queue-item__n" aria-hidden="true">
                  {i + 1}
                </span>
                <Thumb videoId={p.segment.video_id} title={p.work.title} size="s" />
                <div className="queue-item__body">
                  <p className="queue-item__title">
                    <Link to={`/performances/${p.id}`}>{p.work.title}</Link>
                  </p>
                  <p className="queue-item__sub">{performerNames(p)}</p>
                </div>
                <div className="queue-item__actions">
                  <EnqueueButton performance={p} compact />
                  <button
                    type="button"
                    className="icon-btn icon-btn--small"
                    disabled={busy || i === 0}
                    onClick={() => move(i, -1)}
                    aria-label={`「${p.work.title}」を1つ上へ`}
                  >
                    <Icon name="up" />
                  </button>
                  <button
                    type="button"
                    className="icon-btn icon-btn--small"
                    disabled={busy || i === playlist.performance_ids.length - 1}
                    onClick={() => move(i, 1)}
                    aria-label={`「${p.work.title}」を1つ下へ`}
                  >
                    <Icon name="down" />
                  </button>
                  <button
                    type="button"
                    className="icon-btn icon-btn--small"
                    disabled={busy}
                    onClick={() =>
                      save(
                        playlist.performance_ids.filter((_, n) => n !== i),
                        `「${p.work.title}」を外しました`,
                      )
                    }
                    aria-label={`「${p.work.title}」をプレイリストから外す`}
                  >
                    <Icon name="trash" />
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <p className="form__danger">
        <button type="button" className="btn btn-ghost btn-small" onClick={() => setConfirmDelete(true)}>
          <Icon name="trash" className="btn-icon" />
          このプレイリストを削除
        </button>
      </p>
      <ConfirmAction
        open={confirmDelete}
        title={`「${playlist.name}」を削除しますか？`}
        body="プレイリストだけを消します。中の歌唱やお気に入りは残ります。"
        confirmLabel="削除する"
        op="playlists.delete"
        action={() => api.deletePlaylist(playlist.id)}
        onClose={() => setConfirmDelete(false)}
        onDone={() => navigate("/library?tab=playlists")}
      />
    </>
  );
}
